// src/frontend_react/src/pages/UserDashboard/ColumnConfigurationStep.js
import React, { useState, useEffect, useCallback } from 'react';

// Base UI Components
import Button from '../../components/ui/Button/Button';
import Select from '../../components/ui/Select/Select';
import Checkbox from '../../components/ui/Checkbox/Checkbox';
import Alert from '../../components/ui/Alert/Alert';
import Card from '../../components/ui/Card/Card';
import Typography from '../../components/ui/Typography/Typography';
import Stack, { HStack } from '../../components/ui/Stack/Stack';
import Box from '../../components/ui/Box/Box';
import Icon from '../../components/ui/Icon/Icon';


// Local styles
import styles from './ColumnConfigurationStep.module.css';

// React Icons
import {
    FiArrowLeft,
    FiSave,
    FiTag,
    FiTrash2, // Changed from FiTrashAlt as FiTrash2 is more common in Feather
    FiInfo
} from 'react-icons/fi';


function ColumnConfigurationStep({
  allColumns = [],
  initialConfig,
  onConfigSubmit,
  onBack,
  disabled,
  dataSourceType,
}) {
  const [identifierColumn, setIdentifierColumn] = useState('');
  const [additionalPiiToDrop, setAdditionalPiiToDrop] = useState([]);
  const [error, setError] = useState('');
  const [deleteOriginalFile, setDeleteOriginalFile] = useState(true);
  const [outputFormat, setOutputFormat] = useState('csv');

  const getCleanedColumn = useCallback((col) => String(col || "").trim().toUpperCase(), []);

  // Extract potentially changing part of dataSourceType for stable dependency
  const requiresIcForHashing = dataSourceType?.requires_ic_for_hashing;

  useEffect(() => {
    // Commented out initial logic as it might be complex/buggy and setup is now simpler
    // const currentIcFromInitial = initialConfig?.icColumnName || (dataSourceType?.default_ic_column_name || '');
    // let piiFromInitial = [];
    // if (Array.isArray(initialConfig?.piiColumnsToRemove)) {
    //   piiFromInitial = initialConfig.piiColumnsToRemove;
    // } else if (Array.isArray(dataSourceType?.default_pii_columns)) {
    //   piiFromInitial = dataSourceType.default_pii_columns;
    // }
    // setIdentifierColumn(currentIcFromInitial);
    // const cleanedIc = requiresIcForHashing ? getCleanedColumn(currentIcFromInitial) : '';
    // setAdditionalPiiToDrop(piiFromInitial.filter(col => getCleanedColumn(col) !== cleanedIc));

    setDeleteOriginalFile(initialConfig?.deleteOriginalFile !== undefined ? initialConfig.deleteOriginalFile : true);
    setOutputFormat(initialConfig?.outputFormat || 'csv');
    setError('');
    setIdentifierColumn(''); // Reset identifier column on new initialConfig/dataSourceType
    setAdditionalPiiToDrop([]); // Reset PII list
  }, [initialConfig, dataSourceType, getCleanedColumn]); // Removed requiresIcForHashing here as it's derived from dataSourceType

  useEffect(() => {
    if (requiresIcForHashing) { // Use the extracted stable variable
      const cleanedIc = getCleanedColumn(identifierColumn);
      setAdditionalPiiToDrop(prevPii => prevPii.filter(col => getCleanedColumn(col) !== cleanedIc));
      if (error && error.includes("Identifier Column (IC) cannot also be marked")) {
        setError('');
      }
    }
  }, [identifierColumn, requiresIcForHashing, error, getCleanedColumn]); // Added requiresIcForHashing

  const handleIdentifierColumnChange = (newIcValue) => {
    setIdentifierColumn(newIcValue);
  };

  const togglePiiToRemoveStatus = (columnName) => {
    setError('');
    const cleanedColumnName = getCleanedColumn(columnName);
    const cleanedIdentifierColumn = getCleanedColumn(identifierColumn);

    if (requiresIcForHashing && cleanedColumnName === cleanedIdentifierColumn && cleanedIdentifierColumn !== '') {
      setError("The selected IC is automatically handled for removal if hashing is enabled. No need to mark it here.");
      return;
    }

    setAdditionalPiiToDrop(prevPii => {
      const isAlreadyPii = prevPii.some(p => getCleanedColumn(p) === cleanedColumnName);
      if (isAlreadyPii) {
        return prevPii.filter(col => getCleanedColumn(col) !== cleanedColumnName);
      } else {
        return [...prevPii, columnName].sort((a, b) => a.localeCompare(b));
      }
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (disabled) return;

    if (requiresIcForHashing && !identifierColumn) {
      setError('An Identifier Column (IC) must be selected for this data type.');
      return;
    }
    setError('');

    let deleteConfirmedByPrompt = false;
    if (deleteOriginalFile) {
      const originalFileNameForPrompt = initialConfig?.fileObject?.name || "the selected file";
      const confirmMessage = `Confirm secure deletion of the app's temporary working copy of the raw PII file "${originalFileNameForPrompt}" from this machine after processing?\n\nYour original file (if different and located elsewhere) remains untouched by this specific action.\nThis deletion of the app's copy is irreversible.`;
      if (window.confirm(confirmMessage)) {
        deleteConfirmedByPrompt = true;
      } else {
        setError("Original PII file deletion (of app's working copy) was cancelled by user. Processing aborted.");
        return;
      }
    }

    const cleanedSelectedIc = requiresIcForHashing ? getCleanedColumn(identifierColumn) : null;
    const cleanedAdditionalPii = additionalPiiToDrop.map(getCleanedColumn);

    const analyticalToKeep = allColumns.filter(col => {
      const cleanedCol = getCleanedColumn(col);
      if (requiresIcForHashing && cleanedCol === cleanedSelectedIc) return false;
      if (cleanedAdditionalPii.includes(cleanedCol)) return false;
      return true;
    });

    onConfigSubmit({
      icColumnName: identifierColumn,
      piiColumnsToRemove: additionalPiiToDrop,
      analyticalColumnsToKeep: analyticalToKeep,
      deleteOriginalFile: deleteOriginalFile,
      deleteConfirmation: deleteConfirmedByPrompt,
      outputFormat: outputFormat,
      requires_ic_for_hashing: dataSourceType?.requires_ic_for_hashing,
    });
  };

  const cleanedSelectedIcForFilter = requiresIcForHashing ? getCleanedColumn(identifierColumn) : null;
  const cleanedAdditionalPiiForFilter = additionalPiiToDrop.map(getCleanedColumn);

  const availableForPiiMarking = allColumns
    .filter(col => {
      const cleanedCol = getCleanedColumn(col);
      if (requiresIcForHashing && cleanedCol === cleanedSelectedIcForFilter && cleanedSelectedIcForFilter) return false;
      return !cleanedAdditionalPiiForFilter.includes(cleanedCol);
    })
    .sort((a, b) => a.localeCompare(b));

  const calculatedAnalyticalToKeep = allColumns
    .filter(col => {
      const cleanedCol = getCleanedColumn(col);
      if (requiresIcForHashing && cleanedCol === cleanedSelectedIcForFilter && cleanedSelectedIcForFilter) return false;
      return !cleanedAdditionalPiiForFilter.includes(cleanedCol);
    })
    .sort((a, b) => a.localeCompare(b));

  const isLargeFile = initialConfig?.fileObject && initialConfig.fileObject.size > (50 * 1024 * 1024); // 50MB
  const canSubmit = !disabled && (!requiresIcForHashing || !!identifierColumn);

  const icSelectOptions = [
    { value: '', label: '-- Select IC (e.g., NRIC, Member ID) --' },
    ...allColumns.map(col => ({ value: col, label: col }))
  ];
  const outputFormatOptions = [
    { value: 'csv', label: 'CSV (Comma Separated Values)' },
    { value: 'parquet', label: 'Parquet (Columnar, Efficient)' },
  ];

  return (
    <Stack spacing="xl" className={styles.columnConfigContainer}>
      <Box>
        <Typography variant="body1" color="secondary">
          Configure how columns from "<strong>{initialConfig?.fileObject?.name || 'your file'}</strong>" will be processed for the "<strong>{dataSourceType?.display_name}</strong>" data category.
        </Typography>
        <ul className={styles.instructionList}>
          <Typography component="li" variant="body2" color="secondary" gutterBottom>
            {requiresIcForHashing ? (
              "Select the primary Identifier Column (IC). This column's values will be used to generate a unique Hashed_ID for each record. The original IC column will then be securely removed."
            ) : (
              `This data type (${dataSourceType?.name}) does not require an Identifier Column for Hashed_ID generation. All PII should be marked for removal.`
            )}
          </Typography>
          <Typography component="li" variant="body2" color="secondary" gutterBottom>
            Mark any other columns containing Personally Identifiable Information (PII) that should also be completely removed from the output.
          </Typography>
          <Typography component="li" variant="body2" color="secondary">
            All remaining columns will be kept as analytical data (alongside Hashed_ID if generated).
          </Typography>
        </ul>
      </Box>

      <form onSubmit={handleSubmit}>
        <Stack spacing="xl">
          {requiresIcForHashing && (
            <Select
              label="Identifier Column (IC):"
              id="identifierColumnSelect"
              value={identifierColumn}
              onChange={(e) => handleIdentifierColumnChange(e.target.value)}
              options={icSelectOptions}
              disabled={disabled}
              required={true}
              hint={identifierColumn ? `Column '${identifierColumn}' will be hashed & removed.` : "This column is essential for generating Hashed_IDs."}
            />
          )}

          <Stack spacing="md">
            <Typography variant="h4" fontWeight="semibold" color="primary" className={styles.sectionTitle}>
              Identify Additional PII Columns for Complete Removal:
            </Typography>
            <HStack spacing="lg" alignItems="flex-start" className={styles.columnSelectionArea}>
              <Stack spacing="sm" className={styles.columnBox}>
                <Typography variant="h5" fontWeight="semibold">Available for PII Marking ({availableForPiiMarking.length})</Typography>
                <Typography variant="caption" color="secondary">Click a column to mark it as "Additional PII to Remove".</Typography>
                <Box className={styles.columnList}>
                  {availableForPiiMarking.length > 0 ? availableForPiiMarking.map(col => (
                    <Button
                      key={`avail-${col}`}
                      variant="tag"
                      onClick={() => !disabled && togglePiiToRemoveStatus(col)}
                      rightIcon={<Icon as={FiTag} size="sm" />}
                      disabled={disabled}
                      title={`Mark '${col}' as PII to remove`}
                    >
                      {col}
                    </Button>
                  )) : <Typography variant="body2" color="placeholder" align="center" className={styles.emptyListMessage}>All other columns are either the IC or already marked as PII.</Typography>}
                </Box>
              </Stack>

              <Stack spacing="sm" className={styles.columnBox}>
                <Typography variant="h5" fontWeight="semibold">Additional PII to Remove ({additionalPiiToDrop.length})</Typography>
                <Typography variant="caption" color="secondary">Click a column to unmark it (will be kept as analytical data).</Typography>
                <Box className={styles.columnList}>
                  {additionalPiiToDrop.length > 0 ? additionalPiiToDrop.map(col => (
                    <Button
                      key={`pii-add-${col}`}
                      variant="tagSelected"
                      onClick={() => !disabled && togglePiiToRemoveStatus(col)}
                      rightIcon={<Icon as={FiTrash2} size="sm" />}
                      disabled={disabled}
                      title={`Unmark '${col}' (will be kept as analytical)`}
                    >
                      {col}
                    </Button>
                  )) : <Typography variant="body2" color="placeholder" align="center" className={styles.emptyListMessage}>No additional columns marked for PII removal.</Typography>}
                </Box>
              </Stack>
            </HStack>
          </Stack>
          
          <Card title="Configuration Summary" titleIcon={<Icon as={FiInfo} />} className={styles.summaryCard}>
            <ul className={styles.summaryList}>
              {requiresIcForHashing && (
                <li>
                  <Typography variant="body1">
                    <strong>Identifier Column (IC):</strong>{' '}
                    {identifierColumn || <span className={styles.notSelected}>Not Selected - Required</span>}
                  </Typography>
                </li>
              )}
              <li>
                <Typography variant="body1">
                  <strong>Additional PII to Remove:</strong> {additionalPiiToDrop.join(', ') || "None"}
                </Typography>
              </li>
              <li>
                <Typography variant="body1">
                  <strong>Analytical Columns to Keep:</strong>{' '}
                  {calculatedAnalyticalToKeep.join(', ') || 
                   ((requiresIcForHashing && identifierColumn) 
                     ? (calculatedAnalyticalToKeep.length === 0 ? "None (Only Hashed_ID if all others are IC/PII)" : "Issue calculating - check selections") 
                     : "None (Select IC or unmark PII to see analytical columns)")}
                </Typography>
              </li>
            </ul>
          </Card>

          <Checkbox
            label="Securely delete app's temporary working copy of the raw PII file from this machine after successful encryption and output generation."
            id="deleteOriginalFile"
            checked={deleteOriginalFile}
            onChange={(e) => setDeleteOriginalFile(e.target.checked)}
            disabled={disabled}
            hintText="(Recommended. Your original source file on disk is not affected by this action.)"
          />

          <Select
            label="Output File Format:"
            id="outputFormatSelect"
            value={outputFormat}
            onChange={(e) => setOutputFormat(e.target.value)}
            options={outputFormatOptions}
            disabled={disabled}
            hint={isLargeFile && outputFormat === 'csv' ? "Warning: Parquet format is recommended for files over 50MB for better performance." : ""}
          />

          {error && <Alert variant="error" title="Configuration Error">{error}</Alert>}

          <HStack justifyContent="space-between" className={styles.finalActions}>
            <Button
              type="button"
              variant="outline"
              onClick={onBack}
              disabled={disabled}
              leftIcon={<Icon as={FiArrowLeft} />}
            >
              Back (Change File)
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={!canSubmit || disabled}
              leftIcon={<Icon as={FiSave} />}
              isLoading={disabled} // Assuming 'disabled' here implies a loading state from parent
            >
              Prepare & Encrypt File
            </Button>
          </HStack>
        </Stack>
      </form>
    </Stack>
  );
}
export default ColumnConfigurationStep;